import { useCallback, useRef, useState } from "react";

export type SoundType = "new-assignment" | "status-change";

interface UseSoundOptions {
  volume?: number; // 0.0 to 1.0
  enabled?: boolean;
}

/**
 * Hook for playing notification sounds
 * @param options - Configuration options for sound playback
 * @returns Object with playSound function and loading state
 */
export function useSound(options: UseSoundOptions = {}) {
  const { volume = 0.5, enabled = true } = options;
  const audioRefs = useRef<Map<SoundType, HTMLAudioElement>>(new Map());
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Initialize audio element for a specific sound type
   */
  const initAudio = useCallback(
    (soundType: SoundType) => {
      if (!audioRefs.current.has(soundType)) {
        const audio = new Audio(`/sounds/${soundType}.wav`);
        audio.volume = volume;
        audioRefs.current.set(soundType, audio);
      }
      return audioRefs.current.get(soundType)!;
    },
    [volume]
  );

  /**
   * Play a notification sound
   * @param soundType - Type of sound to play
   * @returns Promise that resolves when sound finishes playing
   */
  const playSound = useCallback(
    async (soundType: SoundType): Promise<void> => {
      if (!enabled) {
        return;
      }

      try {
        setIsLoading(true);
        const audio = initAudio(soundType);
        audio.volume = volume;

        // Reset audio to beginning if already playing
        audio.currentTime = 0;

        await audio.play();
      } catch (error) {
        // Handle autoplay restrictions or other errors
        console.warn(`Failed to play sound: ${soundType}`, error);
      } finally {
        setIsLoading(false);
      }
    },
    [enabled, volume, initAudio]
  );

  /**
   * Set volume for all sounds
   */
  const setVolume = useCallback((newVolume: number) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    audioRefs.current.forEach(audio => {
      audio.volume = clampedVolume;
    });
  }, []);

  /**
   * Preload all sound files
   */
  const preloadSounds = useCallback(async () => {
    const soundTypes: SoundType[] = ["new-assignment", "status-change"];
    soundTypes.forEach(soundType => {
      initAudio(soundType);
    });
  }, [initAudio]);

  return {
    playSound,
    setVolume,
    preloadSounds,
    isLoading,
  };
}
